/**
 * TypeScript типы для системы шаблонов карточек объектов.
 *
 * Соответствуют Pydantic-схемам из backend/app/schemas/card_template.py
 */

// ============================================================================
// Enums
// ============================================================================

export type CardTemplateStatus = "draft" | "published" | "archived";
export type CardAppliesTo = "object" | "geometry";
export type CardFieldType =
  | "string"
  | "text"
  | "number"
  | "date"
  | "boolean"
  | "json"
  | "classifier"
  | "classifier_tree"
  | "file"
  | "geometry_link";

export const FIELD_TYPE_LABELS: Record<CardFieldType, string> = {
  string: "Строка",
  text: "Текст",
  number: "Число",
  date: "Дата",
  boolean: "Логическое",
  json: "JSON",
  classifier: "Классификатор",
  classifier_tree: "Классификатор (дерево)",
  file: "Файл",
  geometry_link: "Ссылка на геометрию",
};

export const APPLIES_TO_LABELS: Record<CardAppliesTo, string> = {
  object: "Объект",
  geometry: "Геометрия",
};

export const STATUS_LABELS: Record<CardTemplateStatus, string> = {
  draft: "Черновик",
  published: "Опубликован",
  archived: "Архив",
};

// ============================================================================
// Admin — секции
// ============================================================================

export interface CardTemplateSectionCreate {
  code: string;
  name: string;
  applies_to?: CardAppliesTo | null;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface CardTemplateSection extends CardTemplateSectionCreate {
  id: number;
  template_id: number;
  created_at: string;
  updated_at?: string | null;
  /** Смёржено из card_section_rules / ui_config (runtime) */
  visibility_rules?: Record<string, unknown>;
  permissions_config?: Record<string, unknown>;
}

// ============================================================================
// Admin — поля
// ============================================================================

/** Узел KLS типа виджета (GET /card-templates/meta/field-widget-types). */
export interface CardFieldWidgetTypeItem {
  kls_id: number;
  kls_code: string;
  kls_namef: string;
}

export interface CardTemplateFieldCreate {
  field_key: string;
  label: string;
  description?: string | null;
  applies_to?: CardAppliesTo | null;
  field_type: CardFieldType;
  /** Узел KLS (qual FIELD_TYPES); при сохранении может быть вычислен по field_type */
  field_type_kls_id?: number | null;
  qual_id: number;
  kls_id?: number | null;
  geometry_type_kls_id?: number | null;
  /** Стабильная привязка при сохранении шаблона (приоритетнее section_id). */
  section_code?: string | null;
  section_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  is_multiple?: boolean;
  is_active?: boolean;
  display_order?: number;
  default_value?: Record<string, unknown> | null;
  validation_rules?: Record<string, unknown>;
  ui_config?: Record<string, unknown>;
  visibility_rules?: Record<string, unknown>;
  permissions_config?: Record<string, unknown>;
  /** Для полей уровня геометрии: значение с объекта, если на геометрии пусто */
  inherit_from_object?: boolean;
  /** Фаза 8: наследование для полей уровня object (родительский объект по parent_object_id) */
  inheritance_config?: Record<string, unknown> | null;
  /** Последний применённый профиль правил (фаза 6), метаданные */
  rule_profile_id?: number | null;
}

export interface CardTemplateField extends CardTemplateFieldCreate {
  id: number;
  template_id: number;
  /** kls_code узла field_type_kls (удобство UI) */
  field_type_widget_code?: string | null;
  created_at: string;
  updated_at?: string | null;
}

/** Ограничения этапа 2 (совместимо с backend SpatialRuleConfig). */
export interface GeometrySpatialRuleConfig {
  must_be_inside?: number[];
  cannot_overlap?: number[];
  must_not_intersect?: number[];
  area_bounds?: { min?: number; max?: number };
  length_bounds?: { min?: number; max?: number };
}

/** Правила min/max числа геометрий по семантическому типу (KLS). */
export interface CardTemplateGeometryRuleCreate {
  geometry_semantic_kls_id: number;
  min_count?: number;
  max_count?: number | null;
  is_required?: boolean;
  display_order?: number;
  /** Пространственные правила (JSONB); задаётся вместе с кардинальностью. */
  spatial_config?: GeometrySpatialRuleConfig | null;
}

export interface CardTemplateGeometryRule extends CardTemplateGeometryRuleCreate {
  id: number;
  template_id: number;
  created_at: string;
  updated_at?: string | null;
}

// ============================================================================
// Admin — шаблон
// ============================================================================

export interface CardTemplateCreate {
  code: string;
  name: string;
  description?: string | null;
  /** ID типа объекта (KLS, qual 354). `null` — универсальный шаблон для всех типов. */
  object_kls_id: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  sections?: CardTemplateSectionCreate[];
  fields?: CardTemplateFieldCreate[];
  geometry_rules?: CardTemplateGeometryRuleCreate[];
}

export interface CardTemplateUpdate {
  name?: string;
  description?: string | null;
  object_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
  sections?: CardTemplateSectionCreate[];
  fields?: CardTemplateFieldCreate[];
  geometry_rules?: CardTemplateGeometryRuleCreate[];
}

export interface CardTemplate {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  object_kls_id: number | null;
  organization_kls_id?: number | null;
  is_default: boolean;
  status: CardTemplateStatus;
  version: number;
  is_active: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
}

export interface CardTemplateWithDetails extends CardTemplate {
  sections: CardTemplateSection[];
  fields: CardTemplateField[];
  geometry_rules: CardTemplateGeometryRule[];
}

export interface CardTemplatePublishRequest {
  reason?: string | null;
}

/** Восстановление из архива (POST /card-templates/{id}/restore). */
export interface CardTemplateRestoreRequest {
  reason?: string | null;
  replace_default?: boolean;
  restore_as_non_default?: boolean;
}

export interface CardTemplatePublishResponse {
  template_id: number;
  old_status?: string | null;
  new_status: string;
  published_at?: string | null;
  archived_at?: string | null;
}

export interface CardTemplateListResponse {
  items: CardTemplate[];
  total: number;
  skip: number;
  limit: number;
}

/** POST /card-templates/import — результат по каждому элементу пакета */
export type CardTemplateImportOutcome = "created" | "skipped" | "error";

export interface CardTemplateImportResultItem {
  source_template_id?: number | null;
  code: string;
  outcome: CardTemplateImportOutcome;
  template_id?: number | null;
  detail?: string | null;
}

export interface CardTemplateImportResponse {
  results: CardTemplateImportResultItem[];
}

export type CardTemplateImportOnConflict = "error" | "skip" | "suffix";

// ============================================================================
// Runtime — карточка объекта
// ============================================================================

export interface CardValuePayload {
  field_key: string;
  qual_id: number;
  kls_id?: number | null;
  value_text?: string | null;
  value_numeric?: number | null;
  value_date?: string | null;
  value_boolean?: boolean | null;
  value_json?: Record<string, unknown> | null;
  is_primary?: boolean;
  display_order?: number | null;
  /** GUID ГАР для obj_address; явный null — снять привязку; отсутствие — не менять */
  gar_uuid?: string | null;
}

export interface ObjectCardGeometryValues {
  geometry_id: number;
  values: CardValuePayload[];
}

export interface ObjectCardUpdateRequest {
  object_values: CardValuePayload[];
  geometries: ObjectCardGeometryValues[];
  validate_only?: boolean;
}

/** Тело initial_geometry для POST /objects/with-card (без object_id). */
export interface ObjectCreateInitialGeometryPayload {
  name: string;
  description?: string;
  geojson: GeoJSON.Feature | GeoJSON.Geometry;
  type: string;
  style: Record<string, unknown>;
  parent_id?: number;
  geometry_kls_id: number;
  geometry_primitive_kls_id?: number;
  display_order?: number;
}

/** Создание объекта с карточкой (POST /objects/with-card) */
export interface ObjectCreateWithCardRequest {
  kls_id: number;
  organization_id: number;
  object_values: CardValuePayload[];
  geometries?: ObjectCardGeometryValues[];
  /** Одна геометрия сразу после создания (сценарий «контур → новый объект»). */
  initial_geometry?: ObjectCreateInitialGeometryPayload;
}

export interface CardValidationErrorItem {
  field_key: string;
  applies_to?: string | null;
  geometry_id?: number | null;
  message: string;
  code: string;
  /** Код из каталога мета-правил (numeric_min, required_if, …) для UI */
  rule_type_code?: string | null;
}

export interface ObjectCardFieldValue {
  field_key: string;
  applies_to?: string | null;
  geometry_id?: number | null;
  values: CardValuePayload[];
  display_value?: string | null;
  /** Значение подставлено с уровня объекта (наследование по шаблону) */
  inherited_from_object?: boolean;
  /** Значение с родительского объекта (inheritance_config.parent_object) */
  inherited_from_parent_object?: boolean;
}

export interface ObjectCardGeometryItem {
  id: number;
  name: string;
  type: string;
  /** KLS id семантического типа (сопоставление с geometry_type_kls_id в полях шаблона) */
  geometry_kls_id?: number | null;
  geometry_kls_type?: string | null;
  parent_id?: number | null;
  geojson?: Record<string, unknown> | null;
  values: ObjectCardFieldValue[];
}

export interface ObjectCardResponse {
  object_id: number;
  template?: CardTemplateWithDetails | null;
  object_values: ObjectCardFieldValue[];
  geometries: ObjectCardGeometryItem[];
  validation_errors: CardValidationErrorItem[];
  /** Текущий OBJECTGUID ГАР объекта; null если не привязан */
  gar_uuid?: string | null;
}
